"use client";

import { useSyncExternalStore } from "react";

/**
 * Consent state for a single OneTrust category, using OneTrust's own tristate:
 *
 * - `1` — consent granted
 * - `0` — consent denied
 * - `-1` — not collected (OneTrust has not initialized yet)
 */
export type ConsentStatus = 1 | 0 | -1;

export interface OneTrustConsent {
  strictlyNecessaryCookies: ConsentStatus;
  performanceCookies: ConsentStatus;
  functionalCookies: ConsentStatus;
  /** Advertising / marketing cookies. */
  targetingCookies: ConsentStatus;
}

/**
 * Friendly key → OneTrust category ("Custom Group") id. This repo's OneTrust
 * template uses the standard `C000x` ids (see `custom-window.d.ts`). The mapping
 * is account-configured, so if the OneTrust template ever changes (e.g. to
 * numeric ids like `2`), update it here.
 *
 * Note: `apps/prelum/lib/heap-loader.ts` keeps its own hard-coded copy of the
 * analytics category id; prelum shares little from these libs, so it is not yet
 * driven by this map.
 */
const CATEGORY_IDS = {
  strictlyNecessaryCookies: "C0001",
  performanceCookies: "C0002",
  functionalCookies: "C0003",
  targetingCookies: "C0004",
} as const;

// OneTrust fires this on `window` whenever the visitor changes their choices.
const CONSENT_EVENT = "OneTrustGroupsUpdated";

// Returned before OneTrust initializes and during SSR, when window is absent.
const NOT_COLLECTED: OneTrustConsent = Object.freeze({
  strictlyNecessaryCookies: -1,
  performanceCookies: -1,
  functionalCookies: -1,
  targetingCookies: -1,
});

// useSyncExternalStore requires getSnapshot to return a referentially stable
// value while the underlying data is unchanged; a fresh object each call trips
// its infinite-render guard. So memoize on the raw OnetrustActiveGroups string.
let cachedRaw: string | undefined;
let cachedConsent: OneTrustConsent = NOT_COLLECTED;

function statusFor(wrappedGroups: string, categoryId: string): ConsentStatus {
  // wrappedGroups is comma-delimited with guaranteed leading/trailing commas,
  // so ",C0002," only matches the whole token, never a substring of a longer
  // id like "C00020".
  return wrappedGroups.includes(`,${categoryId},`) ? 1 : 0;
}

function computeConsent(groups: string): OneTrustConsent {
  // Normalize to a single leading/trailing comma so whole-token matching works
  // regardless of how OneTrust delimits the string.
  const wrapped = `,${groups.replace(/^,+|,+$/g, "")},`;
  return {
    strictlyNecessaryCookies: statusFor(wrapped, CATEGORY_IDS.strictlyNecessaryCookies),
    performanceCookies: statusFor(wrapped, CATEGORY_IDS.performanceCookies),
    functionalCookies: statusFor(wrapped, CATEGORY_IDS.functionalCookies),
    targetingCookies: statusFor(wrapped, CATEGORY_IDS.targetingCookies),
  };
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener(CONSENT_EVENT, onChange);
  return () => window.removeEventListener(CONSENT_EVENT, onChange);
}

function getSnapshot(): OneTrustConsent {
  const raw = window.OnetrustActiveGroups;

  // Not a string, or an empty/comma-only string (e.g. "", "," or ",,"): the CMP
  // has not published a decision yet. An initialized OneTrust always includes at
  // least strictly-necessary (C0001, which cannot be denied), so a token-less
  // value can only mean "not collected" — never a blanket denial. Report -1 so
  // consumers can tell "not asked yet" apart from "explicitly declined".
  if (typeof raw !== "string" || raw.replace(/,/g, "") === "") {
    cachedRaw = undefined;
    cachedConsent = NOT_COLLECTED;
    return cachedConsent;
  }

  if (raw !== cachedRaw) {
    cachedRaw = raw;
    cachedConsent = computeConsent(raw);
  }

  return cachedConsent;
}

function getServerSnapshot(): OneTrustConsent {
  return NOT_COLLECTED;
}

/**
 * Reactive OneTrust consent state, keyed by friendly category name. Re-renders
 * when the visitor changes their consent (via the `OneTrustGroupsUpdated`
 * event) and is SSR-safe (every category reads `-1` on the server).
 *
 * @example
 * ```ts
 * const consent = useOneTrustConsent();
 * if (consent.performanceCookies === 1) {
 *   // enable analytics / experiment tooling
 * }
 * ```
 */
export function useOneTrustConsent(): OneTrustConsent {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * Imperative one-shot read of the current consent state, for non-React callers.
 * Prefer {@link useOneTrustConsent} in components. SSR-safe: returns all `-1`
 * when `window` is unavailable.
 */
export function getOneTrustConsent(): OneTrustConsent {
  if (typeof window === "undefined") return NOT_COLLECTED;
  return getSnapshot();
}

/**
 * Imperative subscription to consent changes, for lifecycles that must react
 * *synchronously* — e.g. flipping an SDK kill-switch inside the
 * `OneTrustGroupsUpdated` dispatch, before a React render/effect could run. The
 * callback fires on every change; returns an unsubscribe function. UI code
 * should use {@link useOneTrustConsent} instead.
 */
export function subscribeToOneTrustConsent(onChange: () => void): () => void {
  return subscribe(onChange);
}
